import { convertToQueryParams } from '@/helpers/objects'
import ApiService, { type RequestOptions } from '@/services/api.service'
import { currentDateInUTCString } from '@/services/date.service'

import type { AxiosResponse } from 'axios'
import type {
  AccountType,
  AssetBalanceRequest,
  AssetBalanceResponse,
  AssetHistoryBalanceResponse,
  AssetHistoryBalancesParams,
  AssetHistoryStatsParams,
  AssetHistoryStatsResponse,
  AssetInfoParams,
  AssetInfoPostParams,
  AssetInfoPostResponse,
  AssetInfoResponse,
  BlockchainData,
  BlockchainResponse,
  BlockchainsResponse,
  BlockchainsTableParams,
  BlockchainsTableResponse,
  ClientAssetsTableParams,
  ClientAssetsTableResponse,
  CryptoAssetData,
  CryptoAssetResponse,
  CustomAssetPriceParams,
  CustomAssetPriceResponse,
  CustomEntriesResponse,
  CustomEntriesTableParams,
  CustomEntryData,
  CustomEntryResponse,
  ExchangeData,
  ExchangeResponse,
  ExchangesResponse,
  ExchangesTableParams,
  ExchangesTableResponse,
  ManualEntriesTableParams,
  ManualEntriesTableResponse,
  SupportedReturnType,
  UpdateCryptoAssetData,
  UpdateCustomAssetPriceParams,
  UpdateCustomEntryData,
  UpdateBlockchainData,
  ZeroPriceGTAssetsResponse,
  ClaimablesResponse,
  AssetInfoAssignTagsParams,
  AssetInfoAssignTagsResponse,
  APIAssetsDistributionParams,
  AssetsDistributionResponse,
  AssetsStreamEvent,
  AssetsTableParams,
} from '@/types/services/assets'
import type { EmptyDataResponse } from '@/types/services/common'
import type { ValuationMethodType } from '@/types/services/global'
import type { Tag } from '@/types/services/tags'

function mapTagsToAPITags(tags: Tag[]) {
  return tags.map((tag) => ({
    tag_id: tag.id,
    tag_name: tag.name,
    type: tag.type,
  }))
}

class AssetsService {
  static getAssetsStream(
    params: AssetsTableParams & { return_type: SupportedReturnType },
    onMessage: (event: AssetsStreamEvent) => void,
    onClose?: () => void,
    abortController?: AbortController
  ): Promise<void> {
    return ApiService.getStream(
      '/api/v3/accounts/assets/stream_assets_columns',
      'POST',
      (message) => {
        if (!message.data) {
          return
        }
        let parsed
        try {
          parsed = JSON.parse(message.data)
        } catch {
          return
        }
        onMessage({
          event: message.event as AssetsStreamEvent['event'],
          data: parsed,
        })
      },
      onClose,
      params,
      abortController
    )
  }

  static getClientAssets(
    params: ClientAssetsTableParams,
    options?: RequestOptions
  ): Promise<AxiosResponse<ClientAssetsTableResponse>> {
    return ApiService.post(
      '/api/v1/account/search/get_client_assets_faster',
      {
        aggr: {
          params,
        },
      },
      options
    )
  }

  static getExchangesData(
    params: ExchangesTableParams
  ): Promise<AxiosResponse<ExchangesTableResponse>> {
    return ApiService.post('/api/v1/account/search/exchanges_table', {
      aggr: { params },
    })
  }

  static getBlockchainsData(
    params: BlockchainsTableParams
  ): Promise<AxiosResponse<BlockchainsTableResponse>> {
    return ApiService.post('/api/v1/account/search/blockchains_table', {
      aggr: { params },
    })
  }

  static getManualEntryData(
    params: ManualEntriesTableParams
  ): Promise<AxiosResponse<ManualEntriesTableResponse>> {
    return ApiService.post('/api/v1/account/search/manual_entries_table', {
      aggr: { params },
    })
  }

  static getCustomEntriesData(
    params: CustomEntriesTableParams
  ): Promise<AxiosResponse<CustomEntriesResponse>> {
    return ApiService.post('/api/v1/account/search/icos_table', {
      aggr: { params },
    })
  }

  // NOT USED
  static getBlockchainsWallets(
    entity_id: string
  ): Promise<AxiosResponse<BlockchainsResponse>> {
    const body = {
      filters: [
        {
          column: 'type',
          relations: 'eq',
          value: 'blockchain',
        },
        {
          column: 'entity_id',
          relations: 'eq',
          value: entity_id,
        },
      ],
    }
    return ApiService.post(`/api/v1/account/search`, body)
  }

  static getExchanges(
    entity_id: string
  ): Promise<AxiosResponse<ExchangesResponse>> {
    return ApiService.post(`/api/v1/account/search`, {
      filters: [
        {
          column: 'entity_id',
          relations: 'eq',
          value: entity_id,
        },
        {
          column: 'type',
          relations: 'eq',
          value: 'exchange',
        },
      ],
    })
  }

  // NOT USED
  static createEntry(
    account_id: string,
    type: AccountType,
    data: ExchangeData | BlockchainData | CryptoAssetData | CustomEntryData
  ): Promise<
    AxiosResponse<
      | ExchangeResponse
      | BlockchainResponse
      | CryptoAssetResponse
      | CustomEntryResponse
    >
  > {
    let typeUrl: string | null = null
    switch (type) {
      case 'manual_entry':
        typeUrl = 'manual_entries'
        break
      case 'ico':
        typeUrl = 'icos'
        break
      case 'exchange':
        typeUrl = 'exchanges'
        break
      case 'blockchain':
        typeUrl = 'blockchains'
        break
    }

    return ApiService.post(`/api/v1/accounts/${account_id}/${typeUrl}`, {
      ...data,
      request_timestamp: currentDateInUTCString(),
    })
  }

  static updateEntry(
    account_id: string,
    entry_id: string,
    type: 'exchange',
    data: ExchangeData,
    options?: RequestOptions
  ): Promise<AxiosResponse<ExchangeResponse>>

  static updateEntry(
    account_id: string,
    entry_id: string,
    type: 'blockchain',
    data: UpdateBlockchainData,
    options?: RequestOptions
  ): Promise<AxiosResponse<BlockchainResponse>>

  static updateEntry(
    account_id: string,
    entry_id: string,
    type: 'manual_entry',
    data: UpdateCryptoAssetData,
    options?: RequestOptions
  ): Promise<AxiosResponse<CryptoAssetResponse>>

  static updateEntry(
    account_id: string,
    entry_id: string,
    type: 'ico',
    data: UpdateCustomEntryData,
    options?: RequestOptions
  ): Promise<AxiosResponse<CustomEntryResponse>>

  static updateEntry(
    account_id: string,
    entry_id: string,
    type: AccountType,
    data:
      | ExchangeData
      | BlockchainData
      | UpdateCryptoAssetData
      | UpdateCustomEntryData,
    options?: RequestOptions
  ): Promise<
    AxiosResponse<
      | ExchangeResponse
      | BlockchainResponse
      | CryptoAssetResponse
      | CustomEntryResponse
    >
  > {
    // Create an account
    let typeUrl: string | null = null
    switch (type) {
      case 'manual_entry':
        typeUrl = 'manual_entries'
        break
      case 'ico':
        typeUrl = 'icos'
        break
      case 'exchange':
        typeUrl = 'exchanges'
        break
      case 'blockchain':
        typeUrl = 'blockchains'
        break
    }

    return ApiService.put(
      `/api/v1/accounts/${account_id}/${typeUrl}/${entry_id}`,
      data,
      options
    )
  }

  static getAssetBalance(
    params: AssetBalanceRequest
  ): Promise<AxiosResponse<AssetBalanceResponse>> {
    return ApiService.post('/api/v1/account/search/get_asset_balance', {
      aggr: {
        params,
      },
    })
  }

  static getAssetMarketStatsV2(
    params: AssetHistoryStatsParams,
    type: 'crypto' | 'fiat'
  ): Promise<AxiosResponse<AssetHistoryStatsResponse | EmptyDataResponse>> {
    return ApiService.post(
      `/api/v1/account/search/get_asset_market_stats_v2?type=${type}`,
      {
        aggr: {
          params: {
            accounting_method: 'fifo',
            ...params,
          },
        },
      },
      { expected404: true }
    )
  }

  static getAssetHistoryBalances(
    params: AssetHistoryBalancesParams,
    allowConcurrentRequests = false
  ): Promise<AxiosResponse<AssetHistoryBalanceResponse>> {
    return ApiService.post(
      '/api/v1/account/search/get_asset_history_balances',
      {
        aggr: {
          params,
        },
      },
      { allowConcurrentRequests }
    )
  }

  static getAssetInfo(
    params: AssetInfoParams
  ): Promise<AxiosResponse<AssetInfoResponse>> {
    let queryString = `entity_id=${params.entity_id}`
    if (params.currency_id) {
      queryString += `&currency_id=${params.currency_id}`
    } else if (params.ico_id) {
      queryString += `&ico_id=${params.ico_id}`
    }
    return ApiService.get(`/api/v1/accounts/asset_info?${queryString}`)
  }

  static setAssetInfo(
    params: AssetInfoPostParams
  ): Promise<AxiosResponse<AssetInfoPostResponse>> {
    return ApiService.post('/api/v1/accounts/asset_info', {
      ...params,
      tags: mapTagsToAPITags(params.tags),
      request_timestamp: currentDateInUTCString(),
    })
  }

  static assignAssetInfoTags(
    params: AssetInfoAssignTagsParams
  ): Promise<AxiosResponse<AssetInfoAssignTagsResponse>> {
    const { asset_info_id, ...rest } = params
    return ApiService.post(
      `/api/v1/accounts/asset_info/${asset_info_id}/tags`,
      {
        ...rest,
        tags: mapTagsToAPITags(rest.tags),
      }
    )
  }

  static createCustomAssetPrice(
    params: CustomAssetPriceParams
  ): Promise<AxiosResponse<CustomAssetPriceResponse>> {
    return ApiService.post('/api/v1/accounts/asset_custom_settings', params)
  }

  static updateCustomAssetPrice(
    params: UpdateCustomAssetPriceParams
  ): Promise<AxiosResponse<CustomAssetPriceResponse>> {
    const { id, ...data } = params
    return ApiService.put(`/api/v1/accounts/asset_custom_settings/${id}`, data)
  }

  static removeCustomAssetPrice(
    id: string
  ): Promise<AxiosResponse<EmptyDataResponse>> {
    return ApiService.delete(`/api/v1/accounts/asset_custom_settings/${id}`)
  }

  static sendRequestForSupport(
    message: string,
    type: string
  ): Promise<AxiosResponse<EmptyDataResponse>> {
    return ApiService.post('/api/v2/assets/support_request', {
      type,
      message,
    })
  }

  static getZeroPriceAssets(
    entity_id: string,
    target_currency_ids: string[],
    valuation_method: ValuationMethodType
  ): Promise<AxiosResponse<ZeroPriceGTAssetsResponse>> {
    const query = convertToQueryParams({
      target_currency_ids,
      valuation_method,
    })
    return ApiService.get(
      `/api/v1/accounts/assets_with_zero_price/${entity_id}?${query}`
    )
  }

  static getClaimables(
    entity_id: string
  ): Promise<AxiosResponse<ClaimablesResponse>> {
    return ApiService.get(`/api/v1/accounts/claimables?entity_id=${entity_id}`)
  }

  // Admin variant: any date. The client variant is restricted to confirmed
  // client view dates.
  static getAssetsDistribution(
    params: APIAssetsDistributionParams,
    options?: RequestOptions
  ): Promise<AxiosResponse<AssetsDistributionResponse>> {
    return ApiService.post(
      '/api/v1/account/search/get_assets_distribution',
      {
        aggr: {
          params,
        },
      },
      options
    )
  }

  static getClientAssetsDistribution(
    params: APIAssetsDistributionParams,
    options?: RequestOptions
  ): Promise<AxiosResponse<AssetsDistributionResponse>> {
    return ApiService.post(
      '/api/v1/account/search/get_client_assets_distribution',
      {
        aggr: {
          params,
        },
      },
      options
    )
  }
}

export default AssetsService
